import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiDatabase, FiUsers, FiBarChart2, FiTrendingUp, FiSearch, FiArrowRight, FiMapPin, FiBookOpen, FiHeart, FiDollarSign, FiHome, FiSun } from 'react-icons/fi';
import * as ReactIcons from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import api from '../../utils/api';
import ParticlesSwarm from '../effects/ParticlesSwarm';
import TiltCard from '../effects/TiltCard';
import InteractiveMap from '../elements/InteractiveMap';

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay, ease: [0.165, 0.84, 0.44, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};



const kategoriIcons = {
  pendidikan: <FiBookOpen />, kesehatan: <FiHeart />, ekonomi: <FiDollarSign />,
  infrastruktur: <FiHome />, kependudukan: <FiUsers />, pertanian: <FiSun />,
  pariwisata: <FiMapPin />, sosial: <FiUsers />, lingkungan: <FiSun />, pemerintahan: <FiBarChart2 />
};

const HomePage = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [datasets, setDatasets] = useState([]);
  const [berita, setBerita] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, dsRes, beritaRes] = await Promise.all([
          api.get('/datasets/statistik'),
          api.get('/datasets?limit=6'),
          api.get('/berita?limit=3')
        ]);
        setStats(statsRes.data.data);
        setDatasets(dsRes.data.data);
        setBerita(beritaRes.data.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) window.location.href = `/dataset?search=${encodeURIComponent(search)}`;
  };

  const kecamatanNgada = ['Aimere', 'Bajawa', 'Bajawa Utara', 'Golewa', 'Golewa Barat', 'Golewa Selatan', 'Inerie', 'Jerebuu', 'Riung', 'Riung Barat', 'Soa', 'Wolomeze'];

  return (
    <div>
      {/* HERO */}
      <section className="hero">
        <ParticlesSwarm />
        <div className="hero-overlay" />
        <div className="hero-glow" />
        <div className="hero-glow-2" />
        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <motion.div
            className="hero-content"
            style={{ paddingTop: '100px', paddingBottom: '80px' }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <motion.div
              className="hero-badge"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="dot" /> {t('hero.badge')}
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              {t('hero.title').split(' ').map((word, i) => (
                (word === 'Terpercaya' || word === 'Trusted' || word === 'Unggul' || word === 'Superior') 
                ? <span key={i} className="highlight">{word} </span> 
                : word + ' '
              ))}
            </motion.h1>
            <motion.p
              className="hero-desc"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              {t('hero.desc')}
            </motion.p>
            <motion.form
              onSubmit={handleSearch}
              className="search-bar"
              style={{ marginBottom: '1rem' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              <FiSearch style={{ marginLeft: '1rem', color: 'var(--gray-400)' }} size={20} />
              <input type="text" placeholder={t('hero.search_placeholder')} value={search} onChange={(e) => setSearch(e.target.value)} />
              <button type="submit">{t('hero.search_btn')}</button>
            </motion.form>
            <motion.div
              className="hero-actions"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <Link to="/dataset" className="btn btn-primary btn-lg">{t('hero.explore_btn')} <FiArrowRight /></Link>
              <Link to="/analitik" className="btn btn-secondary btn-lg" style={{ background: 'rgba(255,255,255,0.1)', color: 'white', borderColor: 'rgba(255,255,255,0.2)' }}>{t('nav.analitik')}</Link>
            </motion.div>
            <div className="hero-stats">
              <div className="hero-stat">
                <div className="number">{stats ? <CountUp end={stats.totalDataset} duration={2} /> : '—'}</div>
                <div className="label">{t('home.stats.dataset')}</div>
              </div>
              <div className="hero-stat">
                <div className="number">12</div>
                <div className="label">{t('home.stats.kecamatan')}</div>
              </div>
              <div className="hero-stat">
                <div className="number">{stats ? <CountUp end={stats.byKategori?.length || 10} duration={2} /> : '—'}</div>
                <div className="label">{t('home.stats.kategori')}</div>
              </div>
              <div className="hero-stat">
                <div className="number">100%</div>
                <div className="label">{t('home.stats.open_data')}</div>
              </div>
            </div>
          </motion.div>
        </div>
        {/* SVG Inerie silhouette */}
        <svg className="hero-visual" viewBox="0 0 600 400" style={{ position: 'absolute', right: 0, bottom: 0, width: '55%', opacity: 0.06 }}>
          <path d="M0,400 L50,380 L100,350 L150,320 L180,280 L200,220 L220,180 L250,120 L280,80 L300,40 L320,80 L350,130 L380,190 L400,240 L430,280 L460,310 L500,340 L550,370 L600,390 L600,400 Z" fill="white" />
        </svg>
      </section>

      {/* KATEGORI */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container-wide">
          <AnimatedSection>
            <div className="section-header">
              <div className="overline">{t('footer.data_categories')}</div>
              <h2>{t('home.sectors_title')}</h2>
              <p>{t('home.sectors_desc')}</p>
            </div>
          </AnimatedSection>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: '1rem' }}>
            {['pendidikan', 'kesehatan', 'ekonomi', 'infrastruktur', 'kependudukan', 'pertanian', 'pariwisata', 'sosial', 'lingkungan', 'pemerintahan'].map((kat, i) => (
              <AnimatedSection key={kat}>
                <Link to={`/dataset?kategori=${kat}`} style={{ textDecoration: 'none' }}>
                  <TiltCard className="card" style={{ padding: '1.25rem', textAlign: 'center', animationDelay: `${i * 0.05}s` }}>
                    <div style={{ width: 48, height: 48, borderRadius: 12, margin: '0 auto 0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem' }} className={`stat-icon kat-${kat}`}>
                      {kategoriIcons[kat]}
                    </div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem', textTransform: 'capitalize' }}>
                      {kat}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.25rem' }}>
                      {stats?.byKategori?.find(k => k._id === kat)?.count || 0} {t('home.stats.dataset').toLowerCase()}
                    </div>
                  </TiltCard>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED DATASETS */}
      <section style={{ padding: '4rem 0', background: 'var(--gray-50)' }}>
        <div className="container">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
            <div>
              <h2 style={{ fontSize: '1.8rem', color: 'var(--gray-900)' }}>{t('home.featured_title')}</h2>
              <p style={{ color: 'var(--gray-500)' }}>{t('home.featured_desc')}</p>
            </div>
            <Link to="/dataset" className="btn btn-ghost">{t('home.all_data_btn')} <FiArrowRight size={16} style={{ marginLeft: '0.5rem' }} /></Link>
          </div>

          <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', perspective: '1000px' }}>
            <Link to="/dataset" style={{ textDecoration: 'none' }}>
              <TiltCard className="dataset-card">
                <div style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <span className="card-kategori kat-kesehatan">Kesehatan</span>
                  </div>
                  <h4 style={{ color: 'var(--gray-900)', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Eliminasi Malaria per Kecamatan</h4>
                  <p className="card-desc" style={{ marginBottom: '1rem' }}>Status pencapaian eliminasi transmisi lokal malaria per wilayah kecamatan.</p>
                  <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: '0.75rem', display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                    <span><strong>12</strong> Kecamatan</span>
                    <span><strong>2024</strong> Terkini</span>
                  </div>
                </div>
              </TiltCard>
            </Link>

            <Link to="/dataset" style={{ textDecoration: 'none' }}>
              <TiltCard className="dataset-card">
                <div style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <span className="card-kategori kat-pertanian">Pertanian</span>
                  </div>
                  <h4 style={{ color: 'var(--gray-900)', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Indeks Ketahanan Pangan (IKP)</h4>
                  <p className="card-desc" style={{ marginBottom: '1rem' }}>Indeks komposit ketersediaan dan keterjangkauan pangan di Ngada.</p>
                  <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: '0.75rem', display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                    <span><strong>Tahan</strong> Status</span>
                    <span><strong>High</strong> Reliability</span>
                  </div>
                </div>
              </TiltCard>
            </Link>

            <Link to="/dataset" style={{ textDecoration: 'none' }}>
              <TiltCard className="dataset-card">
                <div style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <span className="card-kategori kat-pemerintahan">Digitalisasi</span>
                  </div>
                  <h4 style={{ color: 'var(--gray-900)', marginBottom: '0.5rem', fontSize: '1.1rem' }}>Inventaris Aplikasi E-Gov OPD</h4>
                  <p className="card-desc" style={{ marginBottom: '1rem' }}>Data transformasi digital dan sistem informasi pelayanan publik.</p>
                  <div style={{ borderTop: '1px solid var(--gray-100)', paddingTop: '0.75rem', display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-400)' }}>
                    <span><strong>20</strong> OPD</span>
                    <span><strong>49</strong> Aplikasi</span>
                  </div>
                </div>
              </TiltCard>
            </Link>
          </div>
        </div>
      </section>

      {/* DATASET TERBARU */}
      <section className="section">
        <div className="container-wide">
          <AnimatedSection>
            <div className="section-header">
              <div className="overline">{t('nav.dataset')}</div>
              <h2>Data Sektoral Terkini</h2>
              <p>Dataset terbaru dari berbagai OPD di lingkungan Pemerintah Kabupaten Ngada</p>
            </div>
          </AnimatedSection>
          <div className="dataset-grid">
            {datasets.map((ds, i) => (
              <AnimatedSection key={ds._id}>
                <Link to={`/dataset/${ds._id}`} className="dataset-card" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className="card-top">
                    <span className={`card-kategori kat-${ds.kategori}`}>{ds.kategori}</span>
                    <h4>{ds.judul}</h4>
                    <p className="card-desc">{ds.deskripsi}</p>
                  </div>
                  <div className="card-bottom">
                    <div>
                      <span className="card-value">{ds.nilaiTerakhir?.toLocaleString('id-ID')}</span>
                      {ds.satuan && <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginLeft: '0.25rem' }}>{ds.satuan}</span>}
                    </div>
                    <div>
                      {ds.perubahanPersen !== 0 && (
                        <span className={`card-change ${ds.perubahanPersen > 0 ? 'positive' : 'negative'}`}>
                          {ds.perubahanPersen > 0 ? '↑' : '↓'} {Math.abs(ds.perubahanPersen)}%
                        </span>
                      )}
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)' }}>{ds.organisasi?.substring(0, 30)}</div>
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: '2rem' }}>
            <Link to="/dataset" className="btn btn-secondary">{t('home.all_data_btn')} <FiArrowRight /></Link>
          </div>
        </div>
      </section>

      {/* KECAMATAN */}
      <section className="section" style={{ background: 'white' }}>
        <div className="container-wide">
          <AnimatedSection>
            <div className="section-header">
              <div className="overline">12 {t('home.stats.kecamatan')}</div>
              <h2>{t('home.kecamatan_title')}</h2>
              <p>{t('home.kecamatan_desc')}</p>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <InteractiveMap />
          </AnimatedSection>
          
          <div className="kecamatan-grid" style={{ marginTop: '2rem' }}>
            {kecamatanNgada.map((kec, i) => (
              <AnimatedSection key={kec}>
                <Link to={`/dataset?kecamatan=${kec}`} style={{ textDecoration: 'none' }}>
                  <div className="kecamatan-card" style={{ animationDelay: `${i * 0.05}s` }}>
                    <FiMapPin style={{ color: 'var(--primary-400)', marginBottom: '0.5rem' }} />
                    <div className="kec-name">{kec}</div>
                    <div className="kec-label">Kecamatan</div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* FITUR */}
      <section className="section section-dark">
        <div className="container">
          <AnimatedSection>
            <div className="section-header">
              <div className="overline" style={{ color: 'var(--primary-300)' }}>{t('home.features_badge')}</div>
              <h2>{t('home.features_title')}</h2>
              <p>{t('home.features_desc')}</p>
            </div>
          </AnimatedSection>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem' }}>
            {[
              { icon: <FiDatabase />, title: t('home.feature_list.integrated'), desc: t('home.feature_list.integrated_desc') },
              { icon: <FiBarChart2 />, title: t('home.feature_list.visual'), desc: t('home.feature_list.visual_desc') },
              { icon: <FiTrendingUp />, title: t('home.feature_list.uptodate'), desc: t('home.feature_list.uptodate_desc') },
              { icon: <FiUsers />, title: t('home.feature_list.multiuser'), desc: t('home.feature_list.multiuser_desc') },
              { icon: <FiSearch />, title: t('home.feature_list.search'), desc: t('home.feature_list.search_desc') },
              { icon: <FiMapPin />, title: t('home.feature_list.per_kec'), desc: t('home.feature_list.per_kec_desc') }
            ].map((f, i) => (
              <AnimatedSection key={i}>
                <div style={{ padding: '1.5rem', borderRadius: 16, background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', animationDelay: `${i * 0.1}s` }}>
                  <div style={{ width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(135deg, var(--primary-600), var(--primary-400))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '1.2rem', marginBottom: '1rem' }}>
                    {f.icon}
                  </div>
                  <h4 style={{ color: 'white', fontFamily: 'var(--font-body)', marginBottom: '0.5rem' }}>{f.title}</h4>
                  <p style={{ fontSize: '0.85rem', lineHeight: 1.6 }}>{f.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* BERITA */}
      {berita.length > 0 && (
        <section className="section">
          <div className="container-wide">
            <AnimatedSection>
              <div className="section-header">
                <div className="overline">{t('home.news_title')}</div>
                <h2>{t('home.news_desc')}</h2>
              </div>
            </AnimatedSection>
            <div className="berita-grid">
              {berita.map((b, i) => {
                const backendUrl = api.defaults.baseURL.replace('/api', '');
                return (
                  <AnimatedSection key={b._id}>
                    <Link to={`/berita/${b._id}`} className="berita-card" style={{ animationDelay: `${i * 0.1}s` }}>
                      <div className="berita-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        {b.gambar ? (
                          <img 
                            src={`${backendUrl}/uploads/${b.gambar}`} 
                            alt={b.judul} 
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<span style="color:rgba(0,0,0,0.1); font-size: 2rem; font-weight: 800">NGADHU</span>`;
                            }}
                          />
                        ) : (
                          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '3rem', fontFamily: 'var(--font-display)', fontWeight: 800 }}>NGADHU</span>
                        )}
                      </div>
                      <div className="berita-body">
                        <div className="berita-date">{new Date(b.createdAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</div>
                        <h4>{b.judul}</h4>
                        <p>{b.ringkasan}</p>
                      </div>
                    </Link>
                  </AnimatedSection>
                );
              })}
            </div>
            <div style={{ textAlign: 'center', marginTop: '2rem' }}>
              <Link to="/berita" className="btn btn-secondary">{t('home.all_news_btn')} <FiArrowRight /></Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section style={{ background: 'linear-gradient(135deg, var(--primary-700), var(--primary-500))', padding: '4rem 0', textAlign: 'center', color: 'white' }}>
        <div className="container">
          <h2 style={{ color: 'white', marginBottom: '1rem' }}>{t('home.cta_title')}</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '2rem', maxWidth: 500, margin: '0 auto 2rem' }}>
            {t('home.cta_desc')}
          </p>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="btn btn-lg" style={{ background: 'white', color: 'var(--primary-700)' }}>{t('home.cta_btn_register')}</Link>
            <Link to="/dataset" className="btn btn-lg" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>{t('home.cta_btn_view')}</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
